import React, { useEffect, useState } from "react";
import AgentsCrumb from "./AgentsCrumb";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import { defaultTimezone } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";
import TriggerFilters from "./TriggerFilters";
import { eventLabel as labelOfEvent, eventOptions, filterConfigFor } from "./triggerCatalog";


const CreateEventAgent = observer(() => {
  const history = useHistory();
  const [eventType, setEventType] = useState("EMAIL_REPLIED");
  const [eventConfig, setEventConfig] = useState({}); // trigger filters, e.g. { toStages: ["Working"] }
  const [name, setName] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [notifyMode, setNotifyMode] = useState("ON_FAILURE");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => { agentsData.fetchTriggerTypes(); agentsData.fetchSequencesLite(); }, []);

  const eventLabel = labelOfEvent(eventType);
  const canSubmit = !!eventType;

  const buildPayload = () => ({
    name: name.trim() || `When ${eventLabel.toLowerCase()}`,
    eventType,
    ...filterConfigFor(eventType, eventConfig),
    sequenceId: sequenceId || undefined,
    notifyMode,
    timezone
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createEventAgent(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  // Open this template's steps in the canvas to tweak before creating.
  const customize = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const draft = await agentsData.composeTemplate("event-agent", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const step = node => (
    <li>
      <span className="ck">✓</span>
      <span>{node}</span>
    </li>
  );

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <AgentsCrumb items={[{ label: "Hire an agent", to: "/agents/new" }, { label: "Set up" }]} />
          <h1 className="ag-page__title">Hire · Event agent</h1>
          <p className="ag-page__sub">Runs the moment your chosen event happens to a contact.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Trigger</div>
          <label className="ag-field">
            <span className="ag-field__label">When this happens<b className="ag-req">required</b></span>
            <AgentSelect
              value={eventType}
              searchable
              searchPlaceholder="Search events…"
              onChange={v => { setEventType(v); setEventConfig({}); }}
              options={eventOptions()}
            />
            <span className="ag-hint">The agent starts from the contact the event happened to.</span>
          </label>
          <TriggerFilters eventType={eventType} config={eventConfig} onChange={setEventConfig} />
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input
              className="ag-input"
              value={name}
              placeholder={`When ${eventLabel.toLowerCase()}`}
              onChange={e => setName(e.target.value)}
            />
          </label>

          <div className="ag-fsec">What happens next</div>
          <label className="ag-field">
            <span className="ag-field__label">Enroll into sequence <span className="ag-optional">optional</span></span>
            <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
              <span className={sequenceName ? "" : "ag-select__ph"}>
                {sequenceName || "Select a sequence…"}
              </span>
              <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            {sequenceName && (
              <button
                type="button"
                className="ag-link"
                style={{ marginTop: 6, alignSelf: "flex-start" }}
                onClick={() => { setSequenceId(""); setSequenceName(""); }}
              >
                Clear, just notify me, don't enroll
              </button>
            )}
            <span className="ag-hint">Email sequences enrich the contact first; LinkedIn-only sequences skip enrichment. Leave empty to only be notified.</span>
          </label>

          <div className="ag-fsec">Notifications</div>
          <label className="ag-field">
            <span className="ag-field__label">Notify me</span>
            <AgentSelect
              value={notifyMode}
              onChange={setNotifyMode}
              options={[
                { value: "ON_FAILURE", label: "Only when a run fails" },
                { value: "DAILY_DIGEST", label: "Daily digest, one summary a day" },
                { value: "OFF", label: "Off, don't email me" }
              ]}
            />
            <span className="ag-hint">
              Event agents can fire often, so important-only (rate-limited) is the default, no per-run spam.
            </span>
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Runs when <b>{eventLabel.toLowerCase()}</b></span>)}
              {step(<span>Skips anyone already handled or on DNC</span>)}
              {sequenceId && step(<span>Enrolls into <b>{sequenceName}</b></span>)}
              {notifyMode !== "OFF" &&
                step(<span>{notifyMode === "DAILY_DIGEST" ? "Emails you a daily digest" : "Emails you when a run fails"}</span>)}
            </ul>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}><i className="material-icons-outlined">check</i>
            {saving ? "Creating…" : "Create agent"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={customize}><i className="material-icons-outlined">tune</i>Customize steps</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}><i className="material-icons-outlined">close</i>Cancel</button>
          <p className="ag-hint" style={{ textAlign: "center" }}>Want to add or change steps first? “Customize steps” opens this in the builder.</p>
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateEventAgent;
